import { authApi, JwtResponse, LoginRequest } from "../api/auth";
import { ActionContext, Module } from "vuex";
import { RootState } from "@/store/index";
import { userApi, UserConfig } from "@/api/users";

export interface AuthState {
  loginSuccess: boolean;
  currentUser: JwtResponse | null;
  currentUserConfig: UserConfig;
  isUser: boolean;
  isAdmin: boolean;
  isProjectManager: boolean;
}

const authModule: Module<AuthState, RootState> = {
  namespaced: true,
  state: () => ({} as AuthState),
  mutations: {
    successCurrentUser(state: AuthState, currentUser: JwtResponse): void {
      state.currentUser = currentUser;
      state.loginSuccess = true;
      window.sessionStorage.setItem("user", JSON.stringify(state));
      state.isAdmin = currentUser.roles.find((r) => r === "ADMIN")
        ? true
        : false;
      state.isUser = currentUser.roles.find((r) => r === "USER") ? true : false;
      state.isProjectManager = currentUser.roles.find((r) => r === "PROJECT_MANAGER")
          ? true
          : false;
      state.currentUserConfig = currentUser.userConfig;
    },
    failedCurrentUser(state: AuthState): void {
      state.currentUser = null;
      state.loginSuccess = false;
    },
    changeUserConfig(state: AuthState, newState: UserConfig): void {
      state.currentUserConfig = newState;
    },
  },
  actions: {
    resetCurrentUser(context: ActionContext<AuthState, RootState>): void {
      context.commit("failedCurrentUser");
    },
    async changeProjectBoardFavorit(
        context: ActionContext<AuthState, RootState>,
        taskboardId: string
    ): Promise<void> {
      if (context.state.currentUser) {
        const newState = (await userApi.getUserById(context.state.currentUser?.id)).userConfig
        if( newState ) {
          newState.favoritProjectboard =
              newState.favoritProjectboard === taskboardId ? null : taskboardId;
          await userApi.updateUserConfig(context.state.currentUser?.id, newState);
          context.commit("changeUserConfig", newState);
        }
      }
    },
    async changeTaskboardFavorit(
      context: ActionContext<AuthState, RootState>,
      taskboardId: string
    ): Promise<void> {
      if (context.state.currentUser) {
        const newState = (await userApi.getUserById(context.state.currentUser?.id)).userConfig
        if( newState ) {
          newState.favoritBlackboard =
              newState.favoritBlackboard === taskboardId ? null : taskboardId;
          await userApi.updateUserConfig(context.state.currentUser?.id, newState);
          context.commit("changeUserConfig", newState);
        }
      }
    },
    async changeVisibleFutureTasks(
      context: ActionContext<AuthState, RootState>,
      visibility: boolean
    ): Promise<void> {
      if (context.state.currentUser) {
        const newState = (await userApi.getUserById(context.state.currentUser?.id)).userConfig
        if( newState ) {
          newState.visibleFutureTasks = visibility;
          await userApi.updateUserConfig(context.state.currentUser?.id, newState);
          context.commit("changeUserConfig", newState);
        }
      }
    },
    async login(
      context: ActionContext<AuthState, RootState>,
      loginRequest: LoginRequest
    ): Promise<boolean> {
      return context.dispatch("resetCurrentUser").then(async () => {
        try {
          const response = await authApi.signin(loginRequest);
          context.commit("successCurrentUser", response);
          return true;
        } catch (e) {
          context.commit("failedCurrentUser");
          return false;
        }
      });
    },
  },
};

export default authModule;
